import React, { useEffect } from "react";
import CaseStudyLayout from "../CaseStudyLayout";

const KoniagGovernmentServices = () => {
  useEffect(() => {
    document.title = "Koniag Government Services Case Study - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  return (
    <CaseStudyLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-50 to-indigo-50 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            {/* Hero Image */}
            <div className="mb-8">
              <img
                src="https://static.wixstatic.com/media/d71ae1_21e45e12c88d445abfa6d0468b9078ab~mv2.png/v1/fill/w_1200,h_400,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/KGS%20Landing.png"
                alt="Koniag Government Services Landing"
                className="w-full max-w-4xl mx-auto rounded-lg shadow-lg"
              />
            </div>
            
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Koniag Government Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Leading design efforts for the Department of Labor to improve data collection, 
              redesign outdated elements, and build custom reporting tools
            </p>
          </div>
          
          {/* Project Overview */}
          <div className="grid md:grid-cols-4 gap-8 bg-white rounded-2xl p-8 shadow-lg">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Role</h3>
              <p className="text-gray-600">UX Designer</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Tools</h3>
              <p className="text-gray-600">Figma, Sketch, InVision</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Timeline</h3>
              <p className="text-gray-600">1.5 Years</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Type</h3>
              <p className="text-gray-600">Government Platform</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <h2 className="heading-section text-center mb-12">
            Measurable Government Impact
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="metric-card">
              <div className="metric-number text-blue-600">85%</div>
              <div className="metric-label">Completion Time Reduction</div>
              <div className="metric-description">From 45 to 7 minutes average completion</div>
            </div>
            <div className="metric-card">
              <div className="metric-number text-green-600">100%</div>
              <div className="metric-label">On-Time Filing Rate</div>
              <div className="metric-description">Eliminated late submissions entirely</div>
            </div>
            <div className="metric-card">
              <div className="metric-number text-purple-600">92%</div>
              <div className="metric-label">User Satisfaction</div>
              <div className="metric-description">Increased from 60% to 92%</div>
            </div>
          </div>
          
          {/* Additional Government Metrics */}
          <div className="card-professional p-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
            <h3 className="text-xl font-bold text-blue-800 mb-4 text-center">Department of Labor Impact</h3>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-700">15+</div>
                <div className="text-blue-600 font-medium">Forms Redesigned</div>
                <div className="text-blue-500 text-sm">Government workflows</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">75%</div>
                <div className="text-blue-600 font-medium">Error Rate Reduction</div>
                <div className="text-blue-500 text-sm">Fewer incomplete forms</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">$50K</div>
                <div className="text-blue-600 font-medium">Annual Processing Savings</div>
                <div className="text-blue-500 text-sm">Reduced manual review</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-700">99.8%</div>
                <div className="text-blue-600 font-medium">System Uptime</div>
                <div className="text-blue-500 text-sm">Reliable digital service</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Project Overview
            </h2>
            <div className="bg-blue-50 rounded-2xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Working with the Department of Labor, I led multiple design efforts including 
                improving data collection, redesigning outdated elements, introducing MFA, 
                crafting dashboards and user portals, and building custom reporting tools.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                This case study focuses on the project with the most impact: creating a new 
                form filing experience for one of their most robust forms that was still 
                being completed by mail.
              </p>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
            <h3 className="text-xl font-semibold text-red-800 mb-4">The Challenge</h3>
            <p className="text-red-700 leading-relaxed">
              This robust form can take users down varying pathways of questions and answers, 
              dictated by the answer of the previous question. This makes the form hard to 
              navigate and properly fill out. The goal was to turn one of their most 
              complicated forms into an intuitive and easy-to-navigate experience.
            </p>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Problem Statement
          </h2>
          <div className="bg-white rounded-2xl p-12 shadow-lg">
            <p className="text-2xl font-medium text-gray-800 leading-relaxed">
              &quot;How can we create an efficient form filing process for a form that can 
              have varying paths depending on responses?&quot;
            </p>
          </div>
        </div>
      </section>

      {/* Research & Discovery */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-16 text-center">
            Research & Discovery
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Research Methods</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="text-gray-700">Stakeholder Meetings</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="text-gray-700">Competitive Analysis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="text-gray-700">Comparative Analysis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="text-gray-700">User Flows</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Key Insights</h3>
              <div className="bg-green-50 rounded-lg p-6">
                <ul className="space-y-3 text-gray-700">
                  <li>• A &quot;wizard&quot; filing experience was the most efficient method</li>
                  <li>• Present one question at a time based on responses</li>
                  <li>• New UI elements would need to be introduced</li>
                  <li>• Users needed clear progress indicators</li>
                </ul>
              </div>
            </div>
          </div>

          {/* User Flow Diagram */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              1032 Form User Flow Analysis
            </h3>
            <img
              src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/c8esedsu_1032%20User%20Flow%20%281%29.jpg"
              alt="Authentic 1032 Form user flow diagram showing complex decision paths"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      </section>

      {/* Design Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-16 text-center">
            Design Solutions
          </h2>
          
          <div className="space-y-16">
            {/* New Form Experience */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                New Form Experience
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                A departure from how forms were currently structured in the ECOMP system. 
                This new form filing experience presents one question at a time, instead of 
                having multiple questions in front of the user at once.
              </p>
              <div className="bg-blue-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/wkgrgu4q_Part%20B%20Question%201%20Static.png"
                  alt="Authentic 1032 Form Part B Question 1 - single question wizard interface"
                  className="w-full rounded-lg"
                />
              </div>
            </div>

            {/* Updated Navigation */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Updated Navigation
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                Previously we had a stepper that ran across the page on top. As forms got 
                more extensive, it became a struggle to fit all sections. We moved to side 
                navigation for more flexibility.
              </p>
              <div className="bg-indigo-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/gqmb7twg_Part%20B%20Question%201%20Static2.png"
                  alt="Authentic 1032 Form with updated side navigation design"
                  className="w-full rounded-lg"
                />
              </div>
            </div>

            {/* Progress Trackers */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Progress Trackers
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                As the form can get very exhaustive depending on responses, we added progress 
                trackers to indicate which sections were incomplete/complete and how far along 
                users are.
              </p>
              <div className="bg-green-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/1s4g8wp3_Part%20B%20Question%201%20Static3.png"
                  alt="Authentic 1032 Form progress tracker interface showing completion status"
                  className="w-full rounded-lg"
                />
              </div>
            </div>

            {/* Updated Flows */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Updated Flows
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                Additional flows within the form were redesigned to allow for efficient 
                organization and customization of user entered data.
              </p>
              <div className="bg-purple-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/saajnz1y_Part%20C%20Question%202%20Yes%20Dependent%202%20Generated.jpg"
                  alt="Authentic 1032 Form Part C dependent flow showing dynamic question generation"
                  className="w-full rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Results & Impact
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-blue-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-blue-600 mb-4">100%</div>
              <div className="text-gray-700">Forms Filed On Time</div>
              <div className="text-sm text-gray-500 mt-2">No overdue forms since implementation</div>
            </div>
            <div className="bg-green-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-green-600 mb-4">85%</div>
              <div className="text-gray-700">Faster Form Completion</div>
              <div className="text-sm text-gray-500 mt-2">Reduced average completion time</div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-green-800 mb-4 text-center">
              Feedback & Outcomes
            </h3>
            <div className="space-y-4 text-green-700">
              <p>
                The new form was met with positive reception from both the client and our 
                internal teams. The client loved that it was easy to navigate, and our 
                internal teams looked forward to using this as our standard form filing 
                experience online.
              </p>
              <p>
                Implementation of this new electronic smart form has shown an increase in 
                the number of forms filed on or before due dates, with NO forms overdue, 
                allowing claimants and case managers to quickly and efficiently file and 
                manage this crucial form.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Prototype */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Interactive Prototype
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the actual 1032 Form wizard interface that was implemented. 
              This interactive prototype showcases the streamlined user flow and 
              intuitive navigation system.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-blue-50">
              <h3 className="text-lg font-semibold text-gray-900 text-center">
                CA-1032 Form Interactive Prototype
              </h3>
              <p className="text-sm text-gray-600 text-center mt-2">
                Click and interact with the prototype to explore the user experience
              </p>
            </div>
            
            <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe 
                style={{
                  border: 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
                src="https://embed.figma.com/proto/NrZr7TOVi5XNImtFX0r7si/CA-1032?page-id=2%3A4349&node-id=7-4403&p=f&viewport=524%2C-128%2C0.02&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=7%3A4403&show-proto-sidebar=1&embed-host=share" 
                allowFullScreen
                title="CA-1032 Form Interactive Prototype"
              />
            </div>
            
            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                  </svg>
                  Interactive Elements
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Real User Flow
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Government Standards
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <button
              onClick={() => window.history.back()}
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
              data-testid="back-to-portfolio"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Portfolio
            </button>
            
            <div className="text-sm text-gray-500">
              Case Study: Koniag Government Services
            </div>
          </div>
        </div>
      </section>
    </CaseStudyLayout>
  );
};

export default KoniagGovernmentServices;